//@flow
import {useMemo} from 'react';

import {
  filterAdmin1,
  filterAdmin2,
  filterAdmin3,
} from '../utils/shipping-advanced/filter-shipping-admins';
import {mergeAdvancedShippingLocations} from '../utils/shipping-advanced/merge-shipping-location';
import {useStoreCurrency} from '../../currency/utils';
import {useShippingMethod} from './use-shipping-method';
import {shippingRateItemAdapter} from './shipping-rate-item-adapter';
import {useAddressSelector} from '../../address/utils/useAddressSelector';

import type {ShippingRateDisplayItem} from '../../../entities/logistic/delivery-method';
import {filterShippingMethods} from './shipping-advanced/filter-shipping-rates';
import {useShippingMethodConfig} from '.';

export function useShippingAddressRates(countryCode: string) {
  const {
    admin1List,
    admin2List,
    admin3List,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
  } = useAddressSelector(countryCode);

  const currency = useStoreCurrency();

  const {shippingMethod} = useShippingMethodConfig();
  const {shippingMethods, isLoading, shippingMethodType} = useShippingMethod(
    countryCode
  );

  const enabledAdminOutsideShipping = Boolean(
    shippingMethodType === 'ADVANCED' &&
      shippingMethod?.storeShippingMethodAdvancedSetting
        ?.enabledAdminOutsideShipping
  );

  const locationConfig =
    shippingMethodType === 'ADVANCED' &&
    shippingMethods &&
    mergeAdvancedShippingLocations(shippingMethods);

  const locationConfigByCountry = locationConfig && locationConfig[countryCode];

  const admin1ListFiltered = useMemo(() => {
    if (enabledAdminOutsideShipping) return admin1List;
    if (shippingMethodType === 'ADVANCED' && locationConfigByCountry) {
      if (typeof locationConfigByCountry === 'boolean') return admin1List;
      return filterAdmin1(admin1List, locationConfigByCountry);
    }

    return admin1List;
  }, [
    shippingMethodType,
    locationConfigByCountry,
    enabledAdminOutsideShipping,
    admin1List,
  ]);

  const admin2ListFiltered = useMemo(() => {
    if (enabledAdminOutsideShipping) return admin2List;
    if (
      shippingMethodType === 'ADVANCED' &&
      locationConfigByCountry &&
      selectedAdmin1
    ) {
      if (typeof locationConfigByCountry === 'boolean') return admin2List;
      return filterAdmin2(
        admin2List,
        String(selectedAdmin1.adminId),
        locationConfigByCountry
      );
    }

    return admin2List;
  }, [
    enabledAdminOutsideShipping,
    admin2List,
    shippingMethodType,
    locationConfigByCountry,
    selectedAdmin1,
  ]);

  const admin3ListFiltered = useMemo(() => {
    if (enabledAdminOutsideShipping) return admin3List;
    if (
      shippingMethodType === 'ADVANCED' &&
      locationConfigByCountry &&
      selectedAdmin1 &&
      selectedAdmin2
    ) {
      if (typeof locationConfigByCountry === 'boolean') return admin3List;
      return filterAdmin3(
        admin3List,
        String(selectedAdmin1.adminId),
        String(selectedAdmin2.adminId),
        locationConfigByCountry
      );
    }

    return admin3List;
  }, [
    enabledAdminOutsideShipping,
    admin3List,
    shippingMethodType,
    locationConfigByCountry,
    selectedAdmin1,
    selectedAdmin2,
  ]);

  const selectAdmin1Id = selectedAdmin1?.adminId;
  const selectAdmin2Id = selectedAdmin2?.adminId;
  const selectAdmin3Id = selectedAdmin3?.adminId;

  const shippingRates: ShippingRateDisplayItem[] = useMemo(() => {
    if (!shippingMethods || !currency) return [];
    const shouldFilter = shippingMethods.some(
      method => method.type === 'store-shipping-method-advanced-admin-group'
    );
    if (!shouldFilter) {
      return shippingRateItemAdapter(shippingMethods, currency.symbol);
    }

    const filteredMethods = filterShippingMethods(
      shippingMethods,
      countryCode,
      selectAdmin1Id,
      selectAdmin2Id,
      selectAdmin3Id
    );

    return shippingRateItemAdapter(filteredMethods, currency.symbol);
  }, [
    countryCode,
    currency,
    selectAdmin1Id,
    selectAdmin2Id,
    selectAdmin3Id,
    shippingMethods,
  ]);

  return {
    admin1List: admin1ListFiltered,
    admin2List: admin2ListFiltered,
    admin3List: admin3ListFiltered,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    isLoading,
    shippingMethods,
    shippingRates,
  };
}
